import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { ExpandingTextInput } from './ExpandingTextInput';
import { lookupGpsAddress } from '@/services/checklist/gpsAddressLookup';
import { GpsAddressError, GPS_ADDRESS_MESSAGES } from '@/services/checklist/gpsAddress';

interface Props {
  scopeId: string;
  fieldId: string;
  label: string;
  value: string;
  editable?: boolean;
  gpsEnabled?: boolean;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}

export const ChecklistGpsAddressField = ({ scopeId, fieldId, label, value, editable = true, gpsEnabled = false, hideLabel = false, onChange }: Props) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [captureRevision, setCaptureRevision] = useState(0);
  const request = useRef<AbortController | null>(null);
  const canLocate = gpsEnabled && Platform.OS !== 'web' && editable;
  const current = useRef({ scopeId, fieldId, value, editable, canLocate, onChange });
  current.current = { scopeId, fieldId, value, editable, canLocate, onChange };

  const cancel = () => { request.current?.abort(); request.current = null; };
  useEffect(() => {
    cancel(); setLoading(false); setMessage('');
    return cancel;
  }, [scopeId, fieldId, editable, canLocate]);


  const locate = async () => {
    if (!current.current.canLocate || request.current) return;
    const started = current.current;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setMessage('');
    const stillCurrent = () => request.current === controller && !controller.signal.aborted
      && current.current.canLocate && current.current.scopeId === started.scopeId
      && current.current.fieldId === started.fieldId && current.current.value === started.value;
    try {
      const address = await lookupGpsAddress(controller.signal);
      if (stillCurrent()) {
        current.current.onChange(address);
        // O multiline nativo pode manter a altura anterior após preenchimento pelo GPS.
        // Recriar somente na captura aceita solicita nova medição, sem afetar a digitação.
        setCaptureRevision(revision => revision + 1);
        setMessage('Confira o endereço e ajuste o número ou complemento, se necessário.');
      }
    } catch (error) {
      if (stillCurrent()) setMessage(error instanceof GpsAddressError ? error.message : GPS_ADDRESS_MESSAGES.unavailable);
    } finally {
      if (request.current === controller) { request.current = null; setLoading(false); }
    }
  };

  return (
    <View style={styles.container}>
      {!hideLabel && <Text style={styles.label}>{label}</Text>}
      <View style={styles.row}>
        <ExpandingTextInput
          key={captureRevision}
          accessibilityLabel={label}
          style={styles.input}
          value={value}
          editable={editable}
          onChangeText={text => {
            if (!current.current.editable) return;
            cancel(); setLoading(false); setMessage('');
            current.current.onChange(text);
          }}
          placeholder={label}
          placeholderTextColor={theme.colors.text.tertiary}
          autoCorrect={false}
          autoCapitalize="sentences"
        />
        {canLocate && <TouchableOpacity
          testID={`gps-address-${fieldId}`}
          accessibilityRole="button"
          accessibilityLabel={`Obter endereço pela localização: ${label}`}
          accessibilityHint="Usa a localização do aparelho para sugerir um endereço editável."
          accessibilityState={{ disabled: loading, busy: loading }}
          disabled={loading}
          onPress={locate}
          style={styles.button}
        >
          {loading ? <ActivityIndicator size="small" color={theme.colors.primary.default} />
            : <MaterialCommunityIcons name="crosshairs-gps" size={22} color={theme.colors.primary.default} />}
        </TouchableOpacity>}
      </View>
      {canLocate && <Text style={styles.hint}>Use o alvo para sugerir o endereço pela sua localização.</Text>}
      {!!message && <Text accessibilityLiveRegion="polite" style={styles.message}>{message}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginBottom: 12 },
  label: { fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
  input: { flex: 1, minWidth: 0, backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm, borderWidth: 1, borderColor: theme.colors.border.light,
    padding: theme.spacing.sm, fontSize: theme.fontSizes.sm, color: theme.colors.text.primary },
  button: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  message: { fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, marginTop: 4 },
  hint: { fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, marginTop: 4 },
});
